/**
 * /dispatch/admin/email-health — Feb 2026
 *
 * Real-time email bounce/open/click triage between Daily Brief refreshes.
 * Backs the suppression list so SA can one-tap stop sending to a dead
 * address. Auto-suppression also kicks in via the resend webhook after
 * 3+ bounces in 30 days — this page just exposes the manual override.
 *
 * Pulls from `db.email_events` (Resend webhook stream) so numbers match
 * the morning brief exactly; refresh interval is operator-driven.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, AlertTriangle, Mail, Ban, Undo2, TrendingUp, TrendingDown, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

function fmtPct(n) { return `${((n || 0) * 100).toFixed(1)}%`; }
function fmtTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

export default function EmailHealthPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [suppressing, setSuppressing] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/email-health?days=${days}`, { withCredentials: true });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email health');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function suppress(email, reason = 'manual from email-health') {
    setSuppressing((p) => ({ ...p, [email]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/email-suppression`, { email, reason }, { withCredentials: true });
      toast.success(`Suppressed ${email}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suppress failed');
    } finally {
      setSuppressing((p) => { const n = { ...p }; delete n[email]; return n; });
    }
  }

  async function bulkSuppressTopBouncers() {
    const count = data?.top_bouncers?.length || 0;
    if (count === 0) {
      toast.info('No bouncers to suppress');
      return;
    }
    const ok = window.confirm(
      `Suppress all ${count} top bouncing addresses?\n\nThey will no longer receive any email from us. You can unsuppress individually later.`
    );
    if (!ok) return;
    setSuppressing((p) => ({ ...p, __bulk__: true }));
    try {
      const { data: res } = await axios.post(
        `${API_URL}/api/admin/email-suppression/bulk-from-bouncers`,
        { days, min_bounces: 1, max_addresses: 50 },
        { withCredentials: true, timeout: 60000 },
      );
      toast.success(
        `Bulk suppressed: ${res.suppressed_count} new, ${res.already_suppressed_count} already on list`,
        { duration: 6000 },
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk suppress failed');
    } finally {
      setSuppressing((p) => { const n = { ...p }; delete n.__bulk__; return n; });
    }
  }

  async function unsuppress(email) {
    setSuppressing((p) => ({ ...p, [email]: true }));
    try {
      await axios.delete(`${API_URL}/api/admin/email-suppression/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`Removed ${email} from suppression`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unsuppress failed');
    } finally {
      setSuppressing((p) => { const n = { ...p }; delete n[email]; return n; });
    }
  }

  const r = data?.range;
  const r24 = data?.last_24h;
  const isCritical = (r24?.bounce_rate || 0) >= 0.20;
  const isWarning = (r24?.bounce_rate || 0) >= 0.05 && (r24?.bounce_rate || 0) < 0.20;

  return (
    <div className="min-h-screen bg-[#050003] text-[#A3C7A8] font-mono">
      <Navbar />
      {/* Header */}
      <div className="border-b border-[#2A0F3D] sticky top-0 bg-[#050003] z-10 dispatch-sticky-top">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/dispatch')} className="text-[#6B8E73] hover:text-[#39FF14] text-xs uppercase" data-testid="email-health-back">
            ← BACK
          </button>
          <h1 className="text-[#39FF14] text-xs uppercase tracking-widest" data-testid="email-health-title">
            // EMAIL HEALTH
          </h1>
          <div className="ml-auto flex items-center gap-2">
            <select
              value={days}
              onChange={(ev) => setDays(parseInt(ev.target.value, 10) || 7)}
              className="bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono text-xs uppercase tracking-wider px-2 py-1.5 rounded-none focus:outline-none focus:border-[#39FF14]"
              data-testid="email-health-range"
            >
              <option value="1">24 hours</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
            <Button
              onClick={fetchData}
              disabled={loading}
              variant="ghost"
              className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider"
              data-testid="email-health-refresh"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">

        {/* CRITICAL banner */}
        {isCritical && (
          <div className="border-2 border-[#FF3B30] bg-[#FF3B30]/10 p-4 flex items-start gap-3" data-testid="email-health-crit-banner">
            <AlertTriangle className="text-[#FF3B30] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[#FF3B30] font-bold text-sm uppercase tracking-widest">⚠ CRITICAL — BOUNCE RATE {fmtPct(r24?.bounce_rate)} (24H)</div>
              <p className="text-[#A3C7A8] text-xs mt-1">
                Resend will throttle / block sending if this persists. Suppress the top bouncers below to stop the bleed.
              </p>
            </div>
          </div>
        )}
        {isWarning && (
          <div className="border border-[#FF9F0A] bg-[#FF9F0A]/10 p-3 flex items-start gap-3" data-testid="email-health-warn-banner">
            <AlertTriangle className="text-[#FF9F0A] shrink-0 mt-0.5 w-4 h-4" />
            <div className="text-[#FF9F0A] text-xs">Elevated bounce rate: {fmtPct(r24?.bounce_rate)} (24h). Review top bouncers.</div>
          </div>
        )}

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Bounce rate (24h)" value={fmtPct(r24?.bounce_rate)} sub={`${r24?.bounced || 0} of ${r24?.sent_total || 0}`} color={isCritical ? '#FF3B30' : isWarning ? '#FF9F0A' : '#39FF14'} icon={isCritical ? TrendingUp : TrendingDown} testId="kpi-bounce-24h" />
          <KPI label={`Bounce rate (${days}d)`} value={fmtPct(r?.bounce_rate)} sub={`${r?.bounced || 0} of ${r?.sent_total || 0}`} color="#A3C7A8" testId="kpi-bounce-range" />
          <KPI label="Open rate" value={fmtPct(r?.open_rate)} sub={`${r?.opened || 0} opens / ${r?.delivered || 0} delivered`} color="#00F0FF" testId="kpi-open-rate" />
          <KPI label="Click rate" value={fmtPct(r?.click_rate)} sub={`${r?.clicked || 0} clicks`} color="#39FF14" testId="kpi-click-rate" />
        </div>

        {/* Secondary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Sent" value={r?.sent_total ?? '—'} sub={`${days}d`} color="#A3C7A8" testId="kpi-sent" />
          <KPI label="Delivered" value={r?.delivered ?? '—'} sub={`${days}d`} color="#39FF14" testId="kpi-delivered" />
          <KPI label="Bounced" value={r?.bounced ?? '—'} sub={`${days}d`} color="#FF3B30" testId="kpi-bounced" />
          <KPI label="Suppressed total" value={data?.suppressed_count_total ?? '—'} sub="all-time, blocks future sends" color="#FF9F0A" testId="kpi-suppressed" />
        </div>

        {/* Top bouncers */}
        <section>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <h2 className="text-[#39FF14] text-xs uppercase tracking-widest">// TOP BOUNCING ADDRESSES ({days}d)</h2>
            {data?.top_bouncers?.length > 0 && (
              <Button
                onClick={bulkSuppressTopBouncers}
                disabled={!!suppressing.__bulk__}
                variant="outline"
                className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none disabled:opacity-50"
                data-testid="bulk-suppress-bouncers"
              >
                <Ban className="w-3 h-3 mr-1" />
                {suppressing.__bulk__ ? 'Suppressing...' : `Suppress all ${data.top_bouncers.length}`}
              </Button>
            )}
          </div>
          {data?.top_bouncers?.length ? (
            <div className="border border-[#2A0F3D] divide-y divide-[#2A0F3D]">
              {data.top_bouncers.map((b) => (
                <div key={b.email} className="p-3 flex items-center justify-between gap-3 hover:bg-[#111A14]" data-testid={`bouncer-${b.email}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate" data-testid={`bouncer-email-${b.email}`}>
                      {b.user_id ? (
                        <Link
                          to={`/dispatch/admin/users?openUser=${b.user_id}&from=email-health`}
                          className="text-[#00F0FF] hover:text-[#39FF14] underline decoration-dotted underline-offset-2 inline-flex items-center gap-1"
                          title={`Open profile for ${b.user_name || b.email}`}
                          data-testid={`bouncer-profile-link-${b.email}`}
                        >
                          <User className="w-3 h-3 shrink-0" />
                          {b.email}
                        </Link>
                      ) : (
                        <span title="No user account found for this address">{b.email}</span>
                      )}
                      {b.user_role && b.user_role !== 'customer' && (
                        <span className="ml-2 text-[10px] text-[#FF9F0A] uppercase">[{b.user_role}]</span>
                      )}
                    </div>
                    <div className="text-[#82937B] text-xs mt-0.5">
                      <span className="text-[#FF3B30]">{b.bounce_count}× bounce</span>
                      {' · '}last {fmtTs(b.last_bounce_at)}
                      {b.user_name && (
                        <span className="text-[#4B6B52]"> · {b.user_name}</span>
                      )}
                      {b.recent_subjects?.length > 0 && (
                        <span className="block truncate text-[#4B6B52] mt-0.5">subjects: {b.recent_subjects.join(' · ')}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => suppress(b.email)}
                    disabled={!!suppressing[b.email]}
                    variant="outline"
                    className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none disabled:opacity-50 shrink-0"
                    data-testid={`bouncer-suppress-${b.email}`}
                  >
                    <Ban className="w-3 h-3 mr-1" />
                    {suppressing[b.email] ? '...' : 'Suppress'}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-[#2A0F3D] p-6 text-center text-[#6B8E73] text-xs">
              No bouncing addresses in the last {days} days.
            </div>
          )}
        </section>

        {/* Recent bounce feed */}
        <section>
          <h2 className="text-[#39FF14] text-xs uppercase tracking-widest mb-2">// RECENT BOUNCES (LAST 20)</h2>
          {data?.recent_bounces?.length ? (
            <div className="border border-[#2A0F3D] divide-y divide-[#2A0F3D] font-mono text-xs">
              {data.recent_bounces.map((ev, i) => (
                <div key={`${ev.email_id || i}`} className="p-2.5 flex items-center justify-between gap-2 hover:bg-[#111A14]">
                  <div className="flex-1 min-w-0">
                    <span className="text-white">{ev.to || '—'}</span>
                    <span className="text-[#4B6B52]"> · {ev.subject || '(no subject)'}</span>
                  </div>
                  <span className="text-[#6B8E73] text-[10px] shrink-0">{fmtTs(ev.at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-[#2A0F3D] p-4 text-center text-[#6B8E73] text-xs">No bounces yet.</div>
          )}
        </section>

        {/* Suppressed list */}
        <section>
          <h2 className="text-[#39FF14] text-xs uppercase tracking-widest mb-2">// SUPPRESSED ADDRESSES ({data?.suppressed_count_total || 0})</h2>
          {data?.suppressed?.length ? (
            <div className="border border-[#2A0F3D] divide-y divide-[#2A0F3D]">
              {data.suppressed.map((s) => (
                <div key={s.email} className="p-3 flex items-center justify-between gap-3 hover:bg-[#111A14]" data-testid={`suppressed-${s.email}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate"><Mail className="inline w-3 h-3 mr-1 -mt-0.5" />{s.email}</div>
                    <div className="text-[#82937B] text-xs mt-0.5 truncate">
                      <span className="text-[#FF9F0A]">{s.source || 'unknown'}</span>
                      {' · '}{s.reason || ''}
                      {' · '}suppressed {fmtTs(s.suppressed_at)}
                    </div>
                  </div>
                  <Button
                    onClick={() => unsuppress(s.email)}
                    disabled={!!suppressing[s.email]}
                    variant="outline"
                    className="border-[#A3C7A8] text-[#A3C7A8] hover:bg-[#A3C7A8] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none disabled:opacity-50 shrink-0"
                    data-testid={`suppressed-unsuppress-${s.email}`}
                  >
                    <Undo2 className="w-3 h-3 mr-1" />
                    Unsuppress
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-[#2A0F3D] p-4 text-center text-[#6B8E73] text-xs">No suppressed addresses.</div>
          )}
        </section>

      </div>
    </div>
  );
}

function KPI({ label, value, sub, color = '#39FF14', icon: Icon, testId }) {
  return (
    <div className="border border-[#2A0F3D] bg-[#0C0612] p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#6B8E73] flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-2xl mt-1 font-mono font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-[#4B6B52] mt-1">{sub}</div>}
    </div>
  );
}
